// src/pages/payments/Checkout.jsx
// FASE 2 (2026-05-22) — Diseño PlanSelect del handoff OTROS (13).zip.
// 4 métodos de pago: Webpay (unique), Suscripción (sub), Khipu, Mach.
// 1 plan mensual. Suscripción → widget Flow embebido en modal bottom sheet.
// Webpay/Khipu/Mach → placeholder FASE 3.
//
// Lógica preservada íntegra:
//   - checkUserBlocked (anti-abuso al mount)
//   - isLegacy / legacyPricing (beta testers)
//   - pendingSubscription recovery (refresh en pleno widget)
//   - mountEpochRef + epoch guards (anti-race en todos los handlers)
//   - formRef.current.submit() + setTimeout 3000 (doc oficial Flow)
//
// Race conditions cubiertas:
//   - mountEpochRef invalida handlers viejos al cerrar/reabrir modal.
//   - Backend lookup pendingSubscription.registerToken == token.
//   - showFlow gate en Effect 3: widget solo monta cuando modal está abierto.

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { firestore, functions } from '../../firebase';
import { useAuth } from '../../auth/AuthContext';
import { useLogoutFlow } from '../../auth/useLogoutFlow';
import { useSmartBack } from '../../auth/useSmartBack';
import SplashScreen from '../../components/SplashScreen';
import { trackInitiateCheckout } from '../../utils/metaPixel';
// PayPal (internacional, independiente del flujo Flow).
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
// Rediseño visual del checkout (handoff_checkout_page) — solo estilos.
import '../../styles/checkout.css';

const fmtDate = (ms) =>
  new Date(ms).toLocaleDateString('es-CL', { day: 'numeric', month: 'long' });
const in5  = fmtDate(Date.now() + 5  * 86400000);
const in30 = fmtDate(Date.now() + 30 * 86400000);

const FLOW_SCRIPT_SANDBOX = 'https://sandbox.flow.cl/app/elements/flow-1.1.0.min.js';
const FLOW_SCRIPT_PROD    = 'https://www.flow.cl/app/elements/flow-1.1.0.min.js';
const FLOW_SUBSCRIBE_CALLBACK_URL =
  'https://southamerica-east1-mati-edge-elite.cloudfunctions.net/flowSubscribeCallback';

// Códigos paymentMethod de Flow.cl para el flujo one-shot.
const PAYMENT_METHODS = {
  webpay: 1,
  khipu: 22,
  mach: 15,
};
// sel del UI → key de PAYMENT_METHODS
const SEL_TO_METHOD = { unique: 'webpay', khipu: 'khipu', mach: 'mach' };

export default function Checkout() {
  const navigate    = useNavigate();
  const { user, userData, hasActiveAccess, loading: authLoading } = useAuth();
  const handleBack  = useSmartBack();
  const { showLogoutSplash, startLogout, handleLogoutComplete } = useLogoutFlow();

  // Meta Pixel: InitiateCheckout al montar el checkout.
  useEffect(() => { trackInitiateCheckout(); }, []);

  const isLegacy = userData?.legacyPricing === true;
  const planId   = isLegacy ? 'monthly_legacy' : 'monthly';
  const subPrice = isLegacy ? '$9.990' : '$19.990';
  const price    = isLegacy ? '$9.990' : '$19.990'; // precio mostrado en las 4 cards

  // sel: qué método de pago tiene el usuario seleccionado
  const [sel, setSel]                     = useState('unique');
  const [showFlow, setShowFlow]           = useState(false);
  const [token, setToken]                 = useState(null);
  const [isSandbox, setIsSandbox]         = useState(null);
  const [widgetStatus, setWidgetStatus]   = useState('init');
  // init | loading_token | loading_script | mounting | waiting_user | submitting | error
  const [error, setError]                 = useState(null);
  const [isBlocked, setIsBlocked]         = useState(null); // null=no chequeado
  // One-shot (Webpay/Khipu/Mach): estado de redirect a Flow externo.
  const [oneShotLoading, setOneShotLoading] = useState(false);
  const [oneShotError, setOneShotError]     = useState(null);

  // noTrial: legacy (trial 0) o cuenta bloqueada por cobro fallido → sin trial,
  // cobro inmediato, sin breakdown verde de "5 días gratis".
  const noTrial = isLegacy || isBlocked;

  const formRef       = useRef(null);
  const mountEpochRef = useRef(0);

  // ─── F5 FASE 2: pre-check anti-abuso al mount ─────────────────────────────
  useEffect(() => {
    if (authLoading || !user || isBlocked !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const callable = httpsCallable(functions, 'checkUserBlocked');
        const result   = await callable();
        if (cancelled) return;
        console.log('[Checkout] checkUserBlocked:', result.data);
        setIsBlocked(Boolean(result.data && result.data.blocked));
      } catch (e) {
        if (cancelled) return;
        console.warn('[Checkout] checkUserBlocked fallback (no fatal):', e.message);
        setIsBlocked(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user, isBlocked]);

  // ─── Fetch token de suscripción ────────────────────────────────────────────
  // FIX 3: solo prefetcheamos el token cuando el método elegido es suscripción.
  // Antes corría siempre y dejaba un pendingSubscription colgado en el user doc
  // aunque el usuario pagara con Webpay/Khipu/Mach (one-shot), lo que confundía
  // a /pago/return. Ahora flowCreatePayment solo se llama si sel==='sub'.
  useEffect(() => {
    if (authLoading || !user) return;
    if (isBlocked === null) return;
    if (sel !== 'sub') return;

    let cancelled = false;
    const myEpoch = ++mountEpochRef.current;
    setError(null);
    setWidgetStatus('loading_token');

    (async () => {
      // Recovery: si hay pendingSubscription con el mismo planId, reusa token.
      let recoveredToken = null;
      let recoveredEnv   = null;
      try {
        const snap    = await getDoc(doc(firestore, 'users', user.uid));
        const u       = (snap.exists() && snap.data()) || {};
        const pending = u.pendingSubscription;
        if (pending && pending.planId === planId && pending.registerToken && myEpoch === 0) {
          recoveredToken = pending.registerToken;
          recoveredEnv   = u.flowEnv;
        }
      } catch (_) {}

      if (cancelled || mountEpochRef.current !== myEpoch) return;

      if (recoveredToken) {
        setToken(recoveredToken);
        setIsSandbox(recoveredEnv === 'sandbox');
        return;
      }

      try {
        const callable = httpsCallable(functions, 'flowCreatePayment');
        const result   = await callable({ planId, origin: window.location.origin });
        if (cancelled || mountEpochRef.current !== myEpoch) return;

        const data = result.data || {};
        console.log('[Checkout] flowCreatePayment step=', data.step, 'plan=', planId);

        if (data.step === 'subscribed') {
          navigate('/pago/return?activated=true');
          return;
        }
        if (data.step === 'embed_widget' && data.token) {
          setToken(data.token);
          setIsSandbox(Boolean(data.isSandbox));
          return;
        }
        if (data.paymentUrl) {
          window.location.href = data.paymentUrl;
          return;
        }
        throw new Error('Respuesta inesperada del servidor.');
      } catch (err) {
        if (cancelled || mountEpochRef.current !== myEpoch) return;
        console.error('[Checkout] flowCreatePayment error:', err);
        setError('No pudimos preparar el plan. Intenta de nuevo.');
        setWidgetStatus('error');
      }
    })();

    return () => { cancelled = true; };
  }, [user, authLoading, navigate, isBlocked, planId, sel]);

  // ─── Montar widget Flow dentro del modal ───────────────────────────────────
  // Solo corre cuando showFlow=true y token disponible.
  // 320ms de delay: la animación del bottom sheet dura 300ms.
  useEffect(() => {
    if (!token || isSandbox === null) return;
    if (widgetStatus !== 'loading_token') return;
    if (!showFlow) return;

    const myEpoch   = mountEpochRef.current;
    const scriptSrc = isSandbox ? FLOW_SCRIPT_SANDBOX : FLOW_SCRIPT_PROD;
    let scriptEl    = document.querySelector(`script[src="${scriptSrc}"]`);
    let mountTimer;

    function doMount() {
      mountTimer = undefined; // limpiar ref antes de setWidgetStatus para que cleanup vea undefined
      if (mountEpochRef.current !== myEpoch) return;
      if (!window.Flow) {
        setError('Flow no se inicializó. Refresca la página.');
        setWidgetStatus('error');
        return;
      }
      try {
        setWidgetStatus('mounting');

        const container = document.getElementById('flow-subscribe-container');
        if (container) container.innerHTML = '';

        const flow      = window.Flow();
        const elements  = flow.elements();
        const subscribe = elements.create('subscribe', { style: { backgroundColor: '#fff' } });
        subscribe.mount('#flow-subscribe-container', token);

        const handlerResult = flow.handleCardSubscribed(subscribe);
        const onResolve = (data) => {
          if (mountEpochRef.current !== myEpoch) {
            console.log('[Checkout] handler stale, ignorando (epoch cambió)');
            return;
          }
          console.log('[Checkout] handleCardSubscribed resolved:', data);
          if (data && (data.error || data.errorCode || data.code)) {
            onError(data);
            return;
          }
          setWidgetStatus('submitting');
          // Per doc oficial Flow: setTimeout(form.submit, 3000).
          setTimeout(() => {
            if (mountEpochRef.current !== myEpoch) return;
            if (formRef.current) {
              console.log('[Checkout] form.submit() → flowSubscribeCallback');
              formRef.current.submit();
            } else {
              setError('Error interno al enviar el formulario.');
              setWidgetStatus('error');
            }
          }, 3000);
        };
        const onError = (e) => {
          if (mountEpochRef.current !== myEpoch) return;
          console.error('[Checkout] handleCardSubscribed rejected:', e);
          setError('No pudimos registrar la tarjeta. ¿Quieres intentar con otra?');
          setWidgetStatus('error');
        };

        if (handlerResult && typeof handlerResult.then === 'function') {
          handlerResult.then(onResolve, onError);
        } else {
          console.warn('[Checkout] handler no thenable:', handlerResult);
          onResolve(handlerResult);
        }

        setWidgetStatus('waiting_user');
      } catch (e) {
        if (mountEpochRef.current !== myEpoch) return;
        console.error('[Checkout] mount widget error:', e);
        setError('No pudimos mostrar el formulario de tarjeta.');
        setWidgetStatus('error');
      }
    }

    if (scriptEl && window.Flow) {
      mountTimer = setTimeout(doMount, 320);
    } else if (!scriptEl) {
      scriptEl          = document.createElement('script');
      scriptEl.src      = scriptSrc;
      scriptEl.async    = true;
      scriptEl.onload   = doMount;
      scriptEl.onerror  = () => {
        if (mountEpochRef.current !== myEpoch) return;
        setError('No pudimos cargar Flow. Revisa tu conexión y vuelve a intentar.');
        setWidgetStatus('error');
      };
      document.body.appendChild(scriptEl);
    } else {
      scriptEl.addEventListener('load', doMount);
    }

    return () => { if (mountTimer) clearTimeout(mountTimer); };
  }, [token, isSandbox, widgetStatus, showFlow]);

  // Cierra el modal e invalida epoch para que handlers pendientes no disparen.
  const handleCloseModal = () => {
    if (widgetStatus === 'submitting') return;
    mountEpochRef.current++;
    const container = document.getElementById('flow-subscribe-container');
    if (container) container.innerHTML = '';
    setError(null);
    setWidgetStatus('loading_token'); // señal para que Effect 3 remonte al próximo open
    setShowFlow(false);
  };

  const handleCtaClick = async () => {
    if (sel === 'sub') {
      setShowFlow(true);
      return;
    }
    // One-shot: Webpay / Khipu / Mach → flowCreateOneShot → redirect externo.
    if (oneShotLoading) return;
    setOneShotError(null);
    setOneShotLoading(true);

    // Defensa extra (el backend flowConfirm también limpia, pero acá lo
    // hacemos antes para que el doc del user quede coherente apenas el
    // user opta por one-shot, incluso si nunca completa el pago). Si el
    // usuario antes seleccionó 'sub' y eso disparó flowCreatePayment
    // (que escribió pendingSubscription + trial_state PENDING_ACTIVATION),
    // esos flags quedarían huérfanos y abrirían un trial fantasma al
    // vincular Telegram (bug Guillermo).
    try {
      await updateDoc(doc(firestore, 'users', user.uid), {
        pendingSubscription: deleteField(),
        trial_state: deleteField(),
        trial_plan_id: deleteField(),
      });
    } catch (cleanupErr) {
      console.warn('[Checkout] cleanup pre-one-shot fallback (no fatal):', cleanupErr.message);
    }

    try {
      const callable = httpsCallable(functions, 'flowCreateOneShot');
      const result = await callable({
        planId, // 'monthly' o 'monthly_legacy' según legacyPricing
        paymentMethod: PAYMENT_METHODS[SEL_TO_METHOD[sel]],
        origin: window.location.origin,
      });
      const data = result.data || {};
      console.log('[Checkout] flowCreateOneShot →', data.commerceOrder);
      if (data.paymentUrl) {
        localStorage.setItem('ee_processing_next', 'payment_success');
        window.location.href = data.paymentUrl;
        return;
      }
      throw new Error('Respuesta inesperada del servidor.');
    } catch (err) {
      console.error('[Checkout] flowCreateOneShot error:', err);
      setOneShotError('No pudimos iniciar el pago. Intenta de nuevo.');
      setOneShotLoading(false);
    }
  };

  if (showLogoutSplash) {
    return <SplashScreen type="logout" onComplete={handleLogoutComplete} />;
  }

  const ctaBg = sel === 'khipu' ? '#7B5EA7' : sel === 'mach' ? '#5B2D8E' : undefined;

  return (
    <div className="mobile-root">
      <Helmet><title>Elige tu plan · Edge Elite</title></Helmet>

      <section className="screen" style={{ paddingTop: 24, paddingBottom: 80 }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <button
            onClick={handleBack}
            aria-label="Volver"
            style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--bg-elev)', border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Elige tu plan</div>
          <button
            type="button"
            onClick={startLogout}
            style={{ marginLeft: 'auto', padding: '8px 12px', borderRadius: 10, background: 'transparent', border: '1px solid var(--line)', color: 'var(--muted)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Cerrar sesión
          </button>
        </div>

        {isLegacy ? (
          /* ═══ Versión BETA TESTER — hero dorado + features ═══ */
          <>
            {/* Hero card dorado */}
            <div style={{ background: 'linear-gradient(140deg, #1A1508 0%, #2A1E06 100%)', border: '1px solid rgba(245,166,35,.25)', borderRadius: 18, padding: 22, marginBottom: 18, position: 'relative', overflow: 'hidden' }}>
              {/* Glow */}
              <div style={{ position: 'absolute', top: -60, right: -60, width: 180, height: 180, borderRadius: 999, background: 'radial-gradient(circle, rgba(245,166,35,.18), transparent 70%)', pointerEvents: 'none' }} />

              <div className="eyebrow" style={{ color: '#F5A623', marginBottom: 14, position: 'relative' }}>
                🏆 BETA TESTER FUNDADOR
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative' }}>
                <div>
                  <div style={{ textDecoration: 'line-through', fontSize: 13, color: 'rgba(245,166,35,.5)', marginBottom: 2 }}>$29.990/mes</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: 38, fontWeight: 800, letterSpacing: '-0.035em', color: '#F5E6C0' }}>$9.990</span>
                    <span style={{ fontSize: 14, color: 'rgba(245,230,192,.6)' }}>/mes</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(245,230,192,.65)', marginTop: 3 }}>precio bloqueado para siempre</div>
                </div>
                <span style={{ background: 'rgba(245,166,35,.18)', color: '#F5A623', fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 999, border: '1px solid rgba(245,166,35,.3)' }}>
                  60% off vitalicio
                </span>
              </div>
            </div>

            {/* Features dorados */}
            <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 14, padding: 16, marginBottom: 18 }}>
              {[
                { t: 'Precio bloqueado para SIEMPRE', sub: 'Mientras mantengas tu suscripción activa' },
                { t: 'Cuando el público suba, tú pagas $9.990', sub: 'Ningún cliente nuevo tendrá este precio' },
                { t: 'Sin trial · Acceso desde día 1', sub: 'Tu cuenta queda activa al instante' },
                { t: 'Cancela cuando quieras', sub: 'Sin penalidades · 1 clic desde el dashboard' },
              ].map((f, i, arr) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--line)' : 0 }}>
                  <div style={{ flexShrink: 0, marginTop: 1 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F5A623" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-11" /></svg>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>{f.t}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{f.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="eyebrow" style={{ marginBottom: 12 }}>Elige cómo pagar</div>
          </>
        ) : (
          <>
            {/* ── Banner: cuenta sin acceso + trial bloqueado (anti-abuso real) ── */}
            {/* blockedTrials se inserta por dos motivos (ver
                membershipValidator.js:476 + flowSubscriptionWebhook.js:307):
                  - ACCESS_EXPIRED_KICK: el cron 6h kickea a CUALQUIER user con
                    accessUntil vencido. NO distingue trial-abuse de cliente
                    legítimo renovando, cortesía vencida o beta vencido.
                  - PAYMENT_FAILED_FINAL: Flow agotó 3 retries de cargo.
                Filtro frontend (sin tocar Flow / membership / callable):
                  - hasActiveAccess true → ya pagó, banner sin sentido.
                  - lastPaymentDate existe → cliente que ya pagó (renovación,
                    no fraude). Caso Andreina.
                  - cortesiaGrantedAt existe → recibió cortesía. Caso Magdita.
                  - legacyPricing true → beta OG. Guard de membership ya los
                    excluye, esto es defense-in-depth.
                  - betaAccess existe → user que tuvo beta alguna vez.
                Solo cuando NINGUNA de estas se cumple, es trial expirado puro
                (caso de abuso real) y se muestra banner. */}
            {isBlocked === true
              && !hasActiveAccess
              && !userData?.lastPaymentDate
              && !userData?.cortesiaGrantedAt
              && !userData?.legacyPricing
              && !userData?.betaAccess && (
              <div style={{ background: 'rgba(251,191,36,.08)', border: '1px solid #fbbf24', borderRadius: 12, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: 'var(--ink)', lineHeight: 1.5 }}>
                ⚠️ <strong>Tu cuenta no es elegible para período de prueba.</strong>{' '}
                Suscríbete directamente para activar tu acceso.
              </div>
            )}

            {/* ── Screen head ── */}
            <div className="screen-head">
              <h2>🔓 Activa tu acceso</h2>
              <p>Elige cómo quieres pagar. La suscripción mensual incluye 5 días gratis. Obtén acceso inmediato a las alertas en Telegram y al dashboard.</p>
            </div>
          </>
        )}

        {/* ── Grupo: Recomendado · suscripción ── */}
        <div className="co-glabel" style={{ marginTop: 8 }}>
          <span className="t">{noTrial ? 'Recomendado · suscripción' : 'Recomendado · prueba gratis 5 días'}</span>
          <span className="ln" />
        </div>
        <div className="co-methods" style={{ marginTop: 18 }}>
          <div className={`co-row green${sel === 'sub' ? ' sel' : ''}`} onClick={() => setSel('sub')}>
            <div className="co-badge trial">{noTrial ? 'Suscripción mensual' : 'Trial gratis 5 días'}</div>
            <div className="co-logo"><img src="/assets/payment/cargo-automatico.png" alt="Cargo Automático" /></div>
            <div className="info">
              <div className="nm">Suscripción mensual</div>
              <div className="ds">Con tarjeta débito, crédito o prepago · {noTrial ? 'cobro inmediato al confirmar' : 'cargo automático al día 6'}</div>
              <div className="perday">Por menos de $670/día</div>
              <span className="co-tag green">{noTrial ? 'Acceso inmediato al confirmar' : 'Única opción con trial gratis'}</span>
            </div>
            <div className={`co-radio${sel === 'sub' ? ' on' : ''}`} />
          </div>
        </div>

        {/* ── Grupo: Pago único · 30 días · sin trial ── */}
        <div className="co-glabel" style={{ marginTop: 22 }}>
          <span className="t">Pago único · 30 días · sin trial</span>
          <span className="ln" />
        </div>
        <div className="co-methods" style={{ marginTop: 18, marginBottom: 18 }}>
          <div className={`co-row${sel === 'unique' ? ' sel' : ''}`} onClick={() => setSel('unique')}>
            <div className="co-badge pop">Más popular</div>
            <div className="co-logo"><img src="/assets/payment/webpay.png" alt="Webpay" /></div>
            <div className="info">
              <div className="nm">Tarjeta de débito/crédito</div>
              <div className="ds">Tarjeta débito o crédito vía Webpay</div>
              <div className="perday">Por menos de $670/día</div>
            </div>
            <div className={`co-radio${sel === 'unique' ? ' on' : ''}`} />
          </div>

          <div className={`co-row${sel === 'khipu' ? ' sel' : ''}`} onClick={() => setSel('khipu')} style={{ '--row-accent': '#7B5EA7', '--row-soft': 'rgba(123,94,167,.08)' }}>
            <div className="co-logo"><img src="/assets/payment/khipu.png" alt="Khipu" /></div>
            <div className="info">
              <div className="nm">Transferencia bancaria</div>
              <div className="ds">Paga desde tu banco · sin tarjeta</div>
              <div className="perday">Por menos de $670/día</div>
              <span className="co-tag purple">Sin tarjeta</span>
            </div>
            <div className={`co-radio${sel === 'khipu' ? ' on' : ''}`} />
          </div>

          <div className={`co-row${sel === 'mach' ? ' sel' : ''}`} onClick={() => setSel('mach')} style={{ '--row-accent': '#5B2D8E', '--row-soft': 'rgba(91,45,142,.08)' }}>
            <div className="co-logo"><img src="/assets/payment/mach.png" alt="Mach" /></div>
            <div className="info">
              <div className="nm">Mach</div>
              <div className="ds">Paga rápido y fácil</div>
              <div className="perday">Por menos de $670/día</div>
            </div>
            <div className={`co-radio${sel === 'mach' ? ' on' : ''}`} />
          </div>
        </div>

        {/* ── Resumen dinámico (antes del CTA, como el handoff) ── */}
        {sel === 'sub' && !noTrial && (
          <div className="co-summary green">
            <div className="co-srow">
              <div><div className="l1">Hoy</div><div className="l2">Solo verificamos tu tarjeta · no cobramos nada</div></div>
              <span className="val big green">$0</span>
            </div>
            <div className="co-srow">
              <div><div className="l1">{in5} — primer cobro</div><div className="l2">Si no cancelas antes · y cada mes siguiente</div></div>
              <span className="val">{subPrice}</span>
            </div>
          </div>
        )}
        {sel === 'sub' && noTrial && (
          <div className="co-summary green">
            <div className="co-srow">
              <div><div className="l1">Hoy — cobro inmediato</div><div className="l2">Acceso al instante, sin período de prueba</div></div>
              <span className="val green">{subPrice}</span>
            </div>
          </div>
        )}
        {(sel === 'unique' || sel === 'khipu' || sel === 'mach') && (
          <>
            <div className="co-summary neutral">
              <div className="co-srow">
                <div><div className="l1">Acceso hasta</div><div className="l2">Pago único · sin renovación automática</div></div>
                <span className="val" style={{ fontSize: 16 }}>{in30}</span>
              </div>
            </div>
            <div className="co-note red">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
              Sin trial · pagas hoy {price}
            </div>
          </>
        )}

        {/* ── CTA primario ── */}
        <div style={{ marginTop: 16 }}>
          <button className={`co-cta${oneShotLoading ? ' busy' : ''}`} style={{ background: ctaBg || 'var(--accent)' }} onClick={handleCtaClick}>
            {oneShotLoading ? 'Redirigiendo a Flow.cl…' : (
              <>
                {sel === 'sub' && (noTrial ? `Activar por ${price}/mes` : 'Empezar 5 días gratis')}
                {sel === 'unique' && `Ir a pagar ${price} con tarjeta`}
                {sel === 'khipu' && `Pagar ${price} con Khipu`}
                {sel === 'mach' && `Pagar ${price} con Mach`}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </>
            )}
          </button>
          {oneShotError && (
            <div className="co-note red" style={{ justifyContent: 'center', marginTop: 8 }}>⚠️ {oneShotError}</div>
          )}
          <div className="co-flow">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Pago seguro vía <strong style={{ color: 'var(--ink)' }}>Flow.cl</strong>{sel === 'sub' && ' · Cancela con 1 clic'}
          </div>
        </div>

        {/* ── PayPal internacional (después del CTA, como el handoff) ── */}
        <div style={{ marginTop: 20 }}>
          <PayPalIntlSection navigate={navigate} user={user} userData={userData} />
        </div>

        {/* ── Trust row ── */}
        <div className="co-trust">
          {['🔒 SSL 256-bit', 'PCI DSS Niv. 1', 'Transbank', 'Flow.cl'].map((t, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>
              {i > 0 && <span className="sep">·</span>}
              <span className={`t ${t === 'Flow.cl' ? 'flow' : ''}`}>{t}</span>
            </span>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════
          Modal Flow.cl — solo suscripción
          Bottom sheet con animación 300ms + widget real embebido.
      ═══════════════════════════════════════════════════════════ */}
      {showFlow && (
        <div
          onClick={handleCloseModal}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(6px)', zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 430, background: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, overflow: 'hidden', animation: 'flow-slide 300ms cubic-bezier(.2,.8,.2,1)' }}
          >
            {/* Drag handle */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 12, paddingBottom: 4 }}>
              <div style={{ width: 36, height: 4, borderRadius: 999, background: '#DDD' }} />
            </div>

            {/* Header: logo + X + progress dots */}
            <div style={{ padding: '12px 20px 14px', borderBottom: '1px solid #F0F0F3', background: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <img src="/assets/payment/webpay-plus.png" style={{ height: 56, width: 'auto', maxWidth: '70%' }} alt="Webpay Plus" />
                <button
                  onClick={handleCloseModal}
                  disabled={widgetStatus === 'submitting'}
                  style={{ width: 28, height: 28, borderRadius: 999, background: '#F5F5F7', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: widgetStatus === 'submitting' ? 'not-allowed' : 'pointer', opacity: widgetStatus === 'submitting' ? 0.4 : 1, flexShrink: 0 }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2.4" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ display: 'flex', gap: 3 }}>
                  {[1,2,3].map(n => <div key={n} style={{ width: 22, height: 3, borderRadius: 999, background: '#00A651' }} />)}
                </div>
                <span style={{ fontSize: 11, color: '#888', fontWeight: 500 }}>Último paso · Ingresa tu tarjeta</span>
              </div>
            </div>

            {/* Body */}
            <div style={{ padding: '16px 20px', overflowY: 'auto', maxHeight: '72vh' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1D1D1F' }}>Débito, crédito o prepago</div>
                <div style={{ fontSize: 11, color: '#16A34A', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-11"/></svg>
                  Todas aceptadas
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <img src="/assets/payment/cards-strip.png" style={{ width: '100%', height: 'auto' }} alt="Tarjetas aceptadas" />
              </div>

              {/* Estados de carga / error / submitting */}
              {(widgetStatus === 'loading_token' || widgetStatus === 'loading_script' || widgetStatus === 'mounting') && (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#888' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
                  <div style={{ fontSize: 13 }}>Cargando formulario seguro…</div>
                </div>
              )}
              {widgetStatus === 'error' && error && (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>⚠️</div>
                  <p style={{ fontSize: 13, color: '#888', marginBottom: 16, lineHeight: 1.5 }}>{error}</p>
                  <button type="button" onClick={() => window.location.reload()} style={{ padding: '10px 20px', borderRadius: 10, background: 'var(--accent)', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14 }}>Volver a intentar</button>
                </div>
              )}
              {widgetStatus === 'submitting' && (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#888' }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>
                  <div style={{ fontSize: 13 }}>Procesando suscripción…</div>
                  <div style={{ fontSize: 11, marginTop: 8 }}>No cierres esta ventana.</div>
                </div>
              )}

              {/* Formulario POST → flowSubscribeCallback con widget Flow */}
              <form
                ref={formRef}
                id="formSubscribe"
                action={FLOW_SUBSCRIBE_CALLBACK_URL}
                method="POST"
                style={{ display: (widgetStatus === 'waiting_user' || widgetStatus === 'mounting') ? 'block' : 'none' }}
              >
                <input type="hidden" name="token" value={token || ''} />
                <div id="flow-subscribe-container" style={{ minHeight: 250, background: '#fff', borderRadius: 12 }} />
              </form>

              {/* Resumen de cobro — verde (trial) o naranja (sin trial: legacy/blocked) */}
              {!noTrial ? (
                <div style={{ background: '#F3FDF6', border: '1.5px solid #86EFAC', borderRadius: 14, padding: '14px 16px', marginTop: 16, marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#14532D' }}>Hoy</div>
                      <div style={{ fontSize: 11, color: '#16A34A', marginTop: 2 }}>Solo verificamos, no cobramos nada</div>
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 36, fontWeight: 800, color: '#16A34A', letterSpacing: '-0.04em', lineHeight: 1 }}>$0</div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: '1px solid #BBF7D0' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>{in5} — primer cobro</div>
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>Si no cancelas antes</div>
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800, color: '#374151', letterSpacing: '-0.035em', lineHeight: 1 }}>{subPrice}</div>
                  </div>
                </div>
              ) : (
                <div style={{ background: '#FFF7ED', border: '1.5px solid #FED7AA', borderRadius: 14, padding: '14px 16px', marginTop: 16, marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#92400E' }}>Hoy — cobro inmediato</div>
                      <div style={{ fontSize: 11, color: '#D97706', marginTop: 2 }}>Al confirmar la tarjeta</div>
                    </div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800, color: '#92400E', letterSpacing: '-0.04em', lineHeight: 1 }}>{subPrice}</div>
                  </div>
                </div>
              )}

              {/* Security chain */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, flexWrap: 'wrap', paddingBottom: 'calc(env(safe-area-inset-bottom,0px) + 6px)' }}>
                {['🔒 SSL 256-bit', 'PCI DSS Niv. 1', 'Regulado CMF'].map((t, i) => (
                  <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>
                    {i > 0 && <span style={{ color: '#DDD', fontSize: 10, margin: '0 4px' }}>·</span>}
                    <span style={{ fontSize: 10, color: '#AAA' }}>{t}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PayPalIntlSection — bloque independiente para pagos internacionales.
// ═══════════════════════════════════════════════════════════════════════════
// Renderiza el botón oficial @paypal/react-paypal-js en modo SUSCRIPCIÓN
// (2026-06-13). Llama al callable `paypalCreateSubscription` para crear la
// suscripción sobre el PLAN_ID, PayPal maneja el popup, y en `onApprove`
// NO hay capture (eso era de Orders) — solo navega a /pago/exito.
//
// Routing post-pago (decisión Mati 2026-06-10 / Opción 1):
// TODOS los pagos pasan SIEMPRE por /pago/exito ("¡Bienvenido a Edge Elite!"),
// igual que Flow one-shot. Desde esa pantalla el botón "Conectar Telegram"
// los lleva a /conectar-telegram. Consistencia con Flow Webpay/Khipu/Mach.
//
// La activación real (accessUntil, lastPaymentPlan, lastPaymentAmount,
// lastPaymentCurrency) la hace el webhook server-side `paypalWebhook` al
// recibir BILLING.SUBSCRIPTION.ACTIVATED (y las renovaciones mensuales vía
// PAYMENT.SALE.COMPLETED). La pantalla /pago/exito lee los campos via
// onSnapshot del AuthContext — si el webhook todavía no llegó muestra "—"
// brevemente, se rellena solo cuando propaga.
//
// Si REACT_APP_PAYPAL_CLIENT_ID no está definido, muestra fallback discreto.
function PayPalIntlSection({ navigate, user, userData }) {
  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID || '';
  const [status, setStatus] = useState('idle'); // idle | creating | captured | success | error
  const [error, setError] = useState(null);
  // Toggle: 'sub' (suscripción, recomendada y default) | 'order' (pago único).
  // UN solo PayPalScriptProvider monta el intent según esta selección.
  const [selectedPlan, setSelectedPlan] = useState('sub');

  // Divisor "O desde fuera de Chile" (handoff .co-intl-div).
  const intlDivider = (
    <div className="co-intl-div">
      <span className="ln" />
      <span className="t">O desde fuera de Chile</span>
      <span className="ln" />
    </div>
  );

  if (!clientId) {
    return (
      <>
        {intlDivider}
        <div className="co-intl" style={{ textAlign: 'center', fontSize: 12, color: 'var(--muted)' }}>
          PayPal próximamente disponible
        </div>
      </>
    );
  }

  const busy = status === 'creating' || status === 'success';

  // ── Pago único (US$20, intent CAPTURE) ──────────────────────────────────
  const createOrder = async () => {
    setStatus('creating');
    setError(null);
    try {
      const call = httpsCallable(functions, 'paypalCreateOrder');
      const result = await call({});
      const orderId = result && result.data && result.data.orderId;
      if (!orderId) throw new Error('Sin orderId desde el servidor');
      return orderId;
    } catch (e) {
      console.error('[PayPal] createOrder error', e);
      setStatus('error');
      setError('No pudimos crear la orden. Intenta de nuevo.');
      throw e;
    }
  };

  const onApproveOrder = async (data, actions) => {
    try {
      // Pago único: capture client-side.
      const cap = await actions.order.capture();
      const u0 = cap && cap.purchase_units && cap.purchase_units[0];
      const c0 = u0 && u0.payments && u0.payments.captures && u0.payments.captures[0];
      const okCap = cap && (cap.status === 'COMPLETED' || (c0 && c0.status === 'COMPLETED'));
      if (!okCap) {
        console.warn('[PayPal] capture sin COMPLETED', cap);
        setStatus('error');
        setError('PayPal no confirmó el cobro. Si te cobraron, escribinos a soporte@edgeelite.cl.');
        return;
      }
      // Otorgar acceso AL INSTANTE: el backend verifica la orden contra PayPal
      // y setea accessUntil. El webhook PAYMENT.CAPTURE.COMPLETED queda como
      // respaldo redundante e idempotente. Best-effort: si esto falla (red,
      // etc.) NO bloqueamos — el cobro fue real y el webhook otorga igual.
      const orderId = (data && data.orderID) || (cap && cap.id) || null;
      if (orderId) {
        try {
          const confirm = httpsCallable(functions, 'paypalConfirmOrder');
          await confirm({ orderId });
        } catch (ce) {
          console.warn('[PayPal] confirmOrder no otorgó en el acto (respaldo: webhook)', ce);
        }
      }
      setStatus('success');
      navigate('/pago/exito');
    } catch (e) {
      console.error('[PayPal] capture error', e);
      setStatus('error');
      setError('No pudimos confirmar el pago. Si te cobraron, escribinos a soporte@edgeelite.cl.');
    }
  };

  // ── Suscripción (US$15/mes, intent SUBSCRIPTION) ────────────────────────
  const createSubscription = async () => {
    setStatus('creating');
    setError(null);
    try {
      const call = httpsCallable(functions, 'paypalCreateSubscription');
      const result = await call({ origin: window.location.origin });
      const subscriptionID = result && result.data && result.data.subscriptionID;
      if (!subscriptionID) throw new Error('Sin subscriptionID desde el servidor');
      return subscriptionID;
    } catch (e) {
      console.error('[PayPal] createSubscription error', e);
      setStatus('error');
      setError('No pudimos crear la suscripción. Intenta de nuevo.');
      throw e;
    }
  };

  const onApproveSubscription = async (data) => {
    // Suscripción: al aprobar NO hay capture client-side. Otorgar acceso AL
    // INSTANTE: el backend verifica la sub contra PayPal (ACTIVE + plan +
    // dueño) y otorga el primer ciclo. El webhook (BILLING.SUBSCRIPTION.ACTIVATED
    // + PAYMENT.SALE.COMPLETED) queda de respaldo redundante e idempotente.
    // Best-effort: si esto falla o el primer cobro aún no liquidó, el webhook
    // otorga igual. Opción 1 (Mati 2026-06-10): TODOS los pagos van a /pago/exito.
    const subscriptionId = (data && data.subscriptionID) || null;
    if (subscriptionId) {
      try {
        const confirm = httpsCallable(functions, 'paypalConfirmSubscription');
        await confirm({ subscriptionId });
      } catch (ce) {
        console.warn('[PayPal] confirmSubscription no otorgó en el acto (respaldo: webhook)', ce);
      }
    }
    setStatus('success');
    navigate('/pago/exito');
  };

  const onError = (e) => {
    console.error('[PayPal] error', e);
    setStatus('error');
    setError('Hubo un problema con PayPal. Intenta de nuevo.');
  };

  // Selección de card: limpia error previo al cambiar de opción.
  const selectPlan = (plan) => {
    if (plan === selectedPlan) return;
    setSelectedPlan(plan);
    if (status === 'error') { setStatus('idle'); setError(null); }
  };

  // Options del ÚNICO PayPalScriptProvider, según la card activa. La key del
  // provider depende de selectedPlan → al cambiar de opción, react-paypal-js
  // re-monta el SDK con el nuevo intent (sin esto cachearía el intent previo).
  const ppOptions = selectedPlan === 'sub'
    ? { 'client-id': clientId, currency: 'USD', intent: 'subscription', vault: true }
    : { 'client-id': clientId, currency: 'USD', intent: 'capture' };

  return (
    <>
      {intlDivider}
      <div className="co-intl">
        <h3>Pago internacional</h3>
        <div className="price">
          <span className="from">Desde</span>
          <span className="strike">35 USD</span>
          <span className="amt">15 USD</span>
        </div>
        <div className="perday">Por solo US$0,50/día</div>
        <p className="desc">Paga con PayPal. Acceso inmediato al dashboard y a Telegram.</p>

        {/* Selección de plan internacional (toggle io-opt; selección azul PayPal) */}
        <div className="co-pay-list">
          <button className={`io-opt${selectedPlan === 'sub' ? ' sel' : ''}`} onClick={() => selectPlan('sub')}>
            <span className="pb-txt">
              <span className="pb-act">Suscripción · 15 USD/mes</span>
              <span className="pb-sub">Renovación automática · sin prueba gratis</span>
            </span>
            <span className="io-radio" />
          </button>
          <button className={`io-opt${selectedPlan === 'order' ? ' sel' : ''}`} onClick={() => selectPlan('order')}>
            <span className="pb-txt">
              <span className="pb-act">Pago único · 20 USD</span>
              <span className="pb-sub">Acceso por 30 días · sin renovación automática</span>
            </span>
            <span className="io-radio" />
          </button>
        </div>

        {/* Botón PayPal real (SDK), debajo del toggle como en el handoff.
            key={selectedPlan} re-monta el SDK con el intent de la opción activa
            (color gold = amarillo PayPal del handoff). */}
        <div className="co-paypal-wrap">
          <PayPalScriptProvider key={selectedPlan} options={ppOptions}>
            {selectedPlan === 'sub' ? (
              <PayPalButtons
                key="sub"
                fundingSource="paypal"
                style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'subscribe', height: 48 }}
                createSubscription={createSubscription}
                onApprove={onApproveSubscription}
                onError={onError}
                disabled={busy}
              />
            ) : (
              <PayPalButtons
                key="order"
                fundingSource="paypal"
                style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay', height: 48 }}
                createOrder={createOrder}
                onApprove={onApproveOrder}
                onError={onError}
                disabled={busy}
              />
            )}
          </PayPalScriptProvider>
        </div>

        <div className="powered">Desarrollado por <span className="pp-word"><span style={{ color: '#003087' }}>Pay</span><span style={{ color: '#009cde' }}>Pal</span></span></div>

        {status === 'creating' && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>Procesando…</div>
        )}
        {status === 'error' && error && (
          <div style={{ marginTop: 10, padding: '10px 12px', background: 'rgba(220,38,38,.06)', border: '1px solid rgba(220,38,38,.2)', borderRadius: 10, fontSize: 12, color: '#DC2626', textAlign: 'center' }}>⚠️ {error}</div>
        )}
      </div>
    </>
  );
}
